import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { getAuthHeaders } from "../utils/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const PLANET_COLORS = {
    Sun: { bg: "rgba(255, 200, 50, 0.15)", border: "rgba(255, 200, 50, 0.4)" },
    Moon: { bg: "rgba(200, 200, 220, 0.15)", border: "rgba(200, 200, 220, 0.4)" },
    Mercury: { bg: "rgba(150, 180, 200, 0.15)", border: "rgba(150, 180, 200, 0.4)" },
    Venus: { bg: "rgba(255, 150, 180, 0.15)", border: "rgba(255, 150, 180, 0.4)" },
    Mars: { bg: "rgba(255, 100, 100, 0.15)", border: "rgba(255, 100, 100, 0.4)" },
    Jupiter: { bg: "rgba(200, 150, 255, 0.15)", border: "rgba(200, 150, 255, 0.4)" },
    Saturn: { bg: "rgba(100, 100, 120, 0.15)", border: "rgba(100, 100, 120, 0.4)" }
};

const ArchitectOfYear = (props) => {
    const { t, language } = useLanguage();
    const birthDate = props.birthDate;
    const birthTime = props.birthTime;
    const birthLat = props.birthLat;
    const birthLng = props.birthLng;
    const ascendantSign = props.ascendantSign;
    const userName = props.userName || "";
    const [profection, setProfection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    // Listen for language changes and reset state to force re-fetch
    useEffect(() => {
        const handleLanguageChange = () => {
            console.log('[ArchitectOfYear] Language changed, resetting state');
            setProfection(null);
            setLoading(true);
            setExpanded(false);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    useEffect(() => {
        if (!birthDate) {
            setLoading(false);
            return;
        }
        
        // COORDINATE GUARD: Don't fire API if we don't have location coordinates
        // This prevents unnecessary 400/422 errors and wasted API calls
        // Check for null, undefined, zero, or invalid coordinates
        if (!birthLat || !birthLng || birthLat === 0 || birthLng === 0) {
            console.log("⏳ ArchitectOfYear: Waiting for valid coordinates before API call (lat:", birthLat, "lng:", birthLng, ")");
            setLoading(false);
            return;
        }
        
        // If we have coordinates and time but no ascendant, calculate it first
        const calculateProfection = async () => {
            let finalAscendant = ascendantSign;
            
            // Try to get ascendant from chart calculation if we have birth details
            // COORDINATE GUARD: Double-check coordinates are valid before chart API call
            if (!finalAscendant && birthLat && birthLng && birthTime && birthLat !== 0 && birthLng !== 0) {
                try {
                    console.log("🔮 ArchitectOfYear: Calculating chart with coords:", birthLat, birthLng);
                    const chartResponse = await fetch(`${API_URL}/api/calculate-chart`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                            birth_date: birthDate,
                            birth_time: birthTime,
                            lat: birthLat,
                            lng: birthLng,
                            zodiac_system: "tropical"
                        })
                    });
                    if (chartResponse.ok) {
                        const chartData = await chartResponse.json();
                        finalAscendant = chartData.big_three?.rising;
                    }
                } catch (e) {
                    console.log("Could not calculate ascendant for profection");
                }
            }
            
            // Now calculate profection with ascendant
            try {
                // Build URL with user_name parameter like TwelveHousesCarousel does
                let profectionUrl = `${API_URL}/api/profection/calculate?lang=${language}`;
                if (userName) {
                    profectionUrl += `&user_name=${encodeURIComponent(userName)}`;
                }
                
                const headers = await getAuthHeaders();
                const response = await fetch(profectionUrl, {
                    method: "POST",
                    headers,
                    credentials: "include",
                    body: JSON.stringify({ 
                        birth_date: birthDate,
                        ascendant_sign: finalAscendant || null
                    })
                });
                if (response.ok) {
                    const data = await response.json();
                    setProfection(data);
                }
            } catch (e) {
                console.log("Profection calculation error");
            } finally {
                // API KILL-SWITCH: Always stop the loading spinner in finally block
                // This ensures the 'thinking' moon stops even on 400/422 errors
                setLoading(false);
            }
        };
        
        calculateProfection();
    }, [birthDate, birthTime, birthLat, birthLng, ascendantSign, language, userName]);

    if (loading) {
        return (
            <div className="w-full px-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-primary/50">♔</span>
                    <h2 className="text-[10px] font-inter text-primary/50 uppercase tracking-[0.25em]">
                        {t('dashboard.architect_of_year')}
                    </h2>
                </div>
                <div className="p-5 rounded-lg border border-primary/20 bg-background/30">
                    <p className="text-xs text-foreground/50 text-center py-4">{t('dashboard.calculating_year')}</p>
                </div>
            </div>
        );
    }

    if (!profection) return null;

    const colors = PLANET_COLORS[profection.time_lord] || PLANET_COLORS.Sun;
    const focusItems = profection.actionable_focus || [];
    const keywords = profection.keywords || [];

    return (
        <motion.div 
            className="w-full px-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
        >
            <div className="flex items-center gap-2 mb-3">
                <span className="text-primary/50">♔</span>
                <h2 className="text-[10px] font-inter text-primary/50 uppercase tracking-[0.25em]">
                    {t('dashboard.architect_of_year')}
                </h2>
            </div>
            
            <div 
                className="relative rounded-lg overflow-hidden cursor-pointer"
                style={{
                    background: `linear-gradient(135deg, ${colors.bg} 0%, rgba(15,12,20,0.95) 100%)`,
                    border: `1px solid ${colors.border}`
                }}
                onClick={() => setExpanded(!expanded)}
                data-testid="architect-of-year-card"
            >
                <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center"
                            style={{ background: colors.bg, border: `2px solid ${colors.border}` }}
                        >
                            <span className="text-2xl">{profection.time_lord_symbol}</span>
                        </div>
                        <div>
                            <h3 className="font-cinzel text-lg" style={{ color: "#D4AF37" }}>
                                {profection.year_title}
                            </h3>
                            <p className="text-[10px] font-inter text-foreground/50 uppercase tracking-wider">
                                {profection.subtitle}
                            </p>
                        </div>
                    </div>
                    <span 
                        className="text-primary/50 transition-transform"
                        style={{ transform: expanded ? "rotate(90deg)" : "rotate(0)" }}
                    >
                        ▶
                    </span>
                </div>
                
                {expanded && (
                    <div className="px-4 pb-4">
                        <div className="mb-3 flex items-center gap-2">
                            <span className="text-[9px] font-inter text-foreground/40 uppercase">{t('architect.theme')}:</span>
                            <span className="px-2 py-0.5 rounded text-xs" style={{ background: colors.bg, color: "#D4AF37" }}>
                                {profection.house_theme}
                            </span>
                        </div>
                        
                        <div className="p-3 rounded-lg mb-3" style={{ background: "rgba(0,0,0,0.3)" }}>
                            <p className="text-sm font-inter text-foreground/80 leading-relaxed italic">
                                "{profection.reading}"
                            </p>
                        </div>
                        
                        <div className="mb-3">
                            <span className="text-[9px] font-inter text-foreground/40 uppercase block mb-2">{t('architect.focus_areas')}</span>
                            <div className="flex flex-wrap gap-2">
                                {focusItems[0] && <span className="px-2 py-1 rounded-full text-[10px] bg-primary/10 text-primary/70 border border-primary/20">{focusItems[0]}</span>}
                                {focusItems[1] && <span className="px-2 py-1 rounded-full text-[10px] bg-primary/10 text-primary/70 border border-primary/20">{focusItems[1]}</span>}
                                {focusItems[2] && <span className="px-2 py-1 rounded-full text-[10px] bg-primary/10 text-primary/70 border border-primary/20">{focusItems[2]}</span>}
                            </div>
                        </div>
                        
                        <div>
                            <span className="text-[9px] font-inter text-foreground/40 uppercase block mb-2">{t('architect.keywords')}</span>
                            <p className="text-[10px] font-inter text-foreground/50">{keywords.join(" • ")}</p>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default ArchitectOfYear;
